import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import ResizableHeader from "../../shared/ResizableHeader";
import TablePagination from "../../shared/TablePagination";
import useAutoAdvancePagination from "../../shared/useAutoAdvancePagination";
import ExportLoader from "../../shared/ExportLoader";
import useReportExport from "../../shared/useReportExport";
import { useSelector } from "react-redux";
import axios from "axios";
import { useDbContext } from "../../../context/DbContext";
import { selectActiveFyid } from "../../../app/financialYearSlice";
import { selectActiveLocation } from "../../../app/locationSlice";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import SherComboBox from "../../../components/ComboBoxes/SherComboBox";
import GridDesign from "../../GridDesign/GridDesign";
import toExcelIcon from "../../../assets/images/ToExcel.png";
import * as XLSX from "xlsx";
import io from "socket.io-client";

// Default columns shown when no data is loaded
const getDefaultColumns = (reportType) => {
  return [
    { key: "SchNo",      columnName: "SchNo",      header: "Scheme No",     width: 110, align: "center", orderNo: 1, show: true },
    { key: "SchemeName", columnName: "SchemeName", header: "Scheme",        width: 130, align: "left",   orderNo: 2, show: true },
    { key: "CustName",   columnName: "CustName",   header: "Customer Name", width: 220, align: "left",   orderNo: 3, show: true },
    { key: "Mobile",     columnName: "Mobile",     header: "Phone Number",  width: 140, align: "left",   orderNo: 4, show: true },
    { key: "OpenDate",   columnName: "OpenDate",   header: "Open Date",     width: 120, align: "center", orderNo: 5, show: true },
    { key: "DueDate",    columnName: "DueDate",    header: "Due Date",      width: 120, align: "center", orderNo: 6, show: true },
    { key: "Amount",     columnName: "Amount",     header: "Amount",        width: 110, align: "right",  orderNo: 7, show: true },
    { key: "PaidAmt",    columnName: "PaidAmt",    header: "Paid Amt",      width: 110, align: "right",  orderNo: 8, show: true },
    { key: "BalAmt",     columnName: "BalAmt",     header: "Balance Amt",   width: 120, align: "right",  orderNo: 9, show: true },
    { key: "Status",     columnName: "Status",     header: "Status",        width: 110, align: "center", orderNo: 10, show: true },
    { key: "Agent",      columnName: "Agent",      header: "Agent",         width: 120, align: "left",   orderNo: 11, show: true },
  ];
};

const getCurrentDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function SchemeOverDue({ onClose, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const activeFyid = useSelector(selectActiveFyid);
  const activeLocation = useSelector(selectActiveLocation);
  const fyid = activeFyid || 0;

  const reportTypeRef = useRef(null);
  const schemeRef     = useRef(null);

  const [schemesList, setSchemesList]           = useState([]);
  const [tableData, setTableData]               = useState([]);
  const [isLoading, setIsLoading]               = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [searchQuery, setSearchQuery]           = useState("");
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [showGridDesignModal, setShowGridDesignModal] = useState(false);

  const [overdueData, setOverdueData] = useState({
    reportType: { value: "Simple", label: "Simple" },
    scheme: { value: 0, label: "All Schemes", dueDay: null },
    dueDate: getCurrentDate(),
  });

  // Fetch all schemes for dropdown
  useEffect(() => {
    const fetchSchemes = async () => {
      try {
        if (!dbCode) return;
        const response = await axios.get(`${apiBaseUrl}/main/getSchemes/${dbCode}`);
        if (response.data?.success && response.data.data) {
          const list = [
            { value: 0, label: "All Schemes", dueDay: null },
            ...response.data.data.map((item) => ({
              value: item.SchemeId,
              label: item.SchemeName,
              dueDay: item.DueDay,
            })),
          ];
          setSchemesList(list);
        }
      } catch (err) {
        console.error("Error fetching schemes:", err);
      }
    };
    fetchSchemes();
  }, [apiBaseUrl, dbCode]);

  // When a scheme is selected, automatically fill its due date
  const handleSchemeSelect = async (opt) => {
    if (!opt || opt.value === 0 || opt.value === "" || opt.value === null) {
      setOverdueData((prev) => ({
        ...prev,
        scheme: { value: 0, label: "All Schemes", dueDay: null },
      }));
      return;
    }

    // Match from schemesList
    let matchedScheme = schemesList.find(
      (s) => String(s.value) === String(opt.value) || (s.label && opt.label && s.label.toLowerCase() === opt.label.toLowerCase())
    );

    let dueDay = matchedScheme?.dueDay ?? matchedScheme?.DueDay ?? opt?.dueDay ?? opt?.DueDay;

    // Fallback: If dueDay is not yet loaded, fetch directly from SchemeManagement table
    if ((dueDay === undefined || dueDay === null) && opt.value) {
      try {
        const res = await axios.get(`${apiBaseUrl}/main/findSchemeById/${dbCode}/${opt.value}`);
        if (res.data?.success && res.data.data) {
          dueDay = res.data.data.DueDay;
        }
      } catch (err) {
        console.error("Error fetching scheme details:", err);
      }
    }

    let nextDueDate = overdueData.dueDate;
    if (dueDay && Number(dueDay) > 0) {
      const cur = overdueData.dueDate ? new Date(overdueData.dueDate) : new Date();
      const year = cur.getFullYear();
      const month = String(cur.getMonth() + 1).padStart(2, "0");
      const day = String(dueDay).padStart(2, "0");
      nextDueDate = `${year}-${month}-${day}`;
    }

    setOverdueData((prev) => ({
      ...prev,
      scheme: {
        ...(matchedScheme || opt),
        value: opt.value,
        label: opt.label,
        dueDay: dueDay ? Number(dueDay) : null,
      },
      dueDate: nextDueDate,
    }));
  };

  const reportType = overdueData.reportType?.value || "Simple";
  const formName   = `Scheme OverDue - ${reportType}`;

  const [gridColumns, setGridColumns] = useState(() => getDefaultColumns(reportType));

  // Load cached columns on reportType change
  useEffect(() => {
    const cached = localStorage.getItem(`grid_design_${formName}`);
    if (cached) {
      try { setGridColumns(JSON.parse(cached)); return; }
      catch (e) { console.error("Error parsing cached grid settings:", e); }
    }
    setGridColumns(getDefaultColumns(reportType));
  }, [reportType, formName]);

  // Load/merge columns from DB after data arrives
  useEffect(() => {
    const loadGridSettings = async () => {
      if (!dbCode || tableData.length === 0) return;
      const keys = Object.keys(tableData[0]);

      const defaults = keys.map((key, idx) => {
        let header = key, width = 130, align = "left";
        const lk = key.toLowerCase();
        if (lk === "schno")                                         { header = "Scheme No";     width = 110; align = "center"; }
        else if (lk === "schemename")                               { header = "Scheme";        width = 130; }
        else if (lk === "custname")                                 { header = "Customer Name"; width = 220; }
        else if (lk === "mobile" || lk === "phone")                 { header = "Phone Number";  width = 140; }
        else if (lk === "opendate")                                 { header = "Open Date";     width = 120; align = "center"; }
        else if (lk === "duedate" || lk === "date")                 { header = "Due Date";      width = 120; align = "center"; }
        else if (lk.includes("amt") || lk.includes("amount") || lk === "balamt") { width = 120; align = "right"; }
        else if (lk === "status")                                   { header = "Status";        width = 100; align = "center"; }
        else if (lk === "agent")                                    { header = "Agent";         width = 120; }
        return { key, columnName: key, header, width, align, orderNo: idx, show: true };
      });

      const cached = localStorage.getItem(`grid_design_${formName}`);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (keys.every((k) => parsed.map((c) => c.key).includes(k))) { setGridColumns(parsed); return; }
        } catch (e) { console.error("Error parsing cached grid settings:", e); }
      }

      try {
        const response = await axios.get(`${apiBaseUrl}/main/griddesign/${dbCode}/${formName}`);
        const dbCols = response.data.recordset || response.data || [];
        if (dbCols.length > 0) {
          const merged = defaults.map((col) => {
            const dbMatch = dbCols.find((c) => String(c.ColName).toLowerCase() === String(col.columnName).toLowerCase());
            if (dbMatch) return { ...col, width: dbMatch.Width ?? col.width, orderNo: dbMatch.InNo ?? col.orderNo, header: dbMatch.Header || col.header, show: dbMatch.Vis === 1 };
            return col;
          });
          merged.sort((a, b) => a.orderNo - b.orderNo);
          localStorage.setItem(`grid_design_${formName}`, JSON.stringify(merged));
          setGridColumns(merged);
        } else { setGridColumns(defaults); }
      } catch (err) { console.error("Error loading grid settings from DB:", err); setGridColumns(defaults); }
    };
    loadGridSettings();
  }, [tableData, dbCode, apiBaseUrl, formName]);

  const visibleColumns = useMemo(() => gridColumns.filter((c) => c.show), [gridColumns]);
  const gridTemplateColumns = useMemo(() => `60px ` + visibleColumns.map((c) => `${c.width}px`).join(" "), [visibleColumns]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const params = {
        reportType: overdueData.reportType ? [overdueData.reportType.value, overdueData.reportType.label] : ["Simple", "Simple"],
        dueDate: overdueData.dueDate || "",
        fyid: fyid,
        locationId: activeLocation ? activeLocation.id : "",
        schemeId: overdueData.scheme?.value || 0,
      };
      const response = await axios.get(`${apiBaseUrl}/main/SchOverdueList/${dbCode}`, {
        params,
        paramsSerializer: (p) =>
          Object.entries(p).map(([key, val]) =>
            Array.isArray(val)
              ? val.map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join("&")
              : `${encodeURIComponent(key)}=${encodeURIComponent(val)}`
          ).join("&"),
      });
      const data = response.data || [];
      setTableData(data);
      if (data.length > 0) setIsSidebarExpanded(false);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const socket = io(apiBaseUrl);
    socket.on("dataUpdated", () => {});
    return () => socket.disconnect();
  }, [apiBaseUrl]);

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return tableData;
    const q = searchQuery.toLowerCase().trim();
    return tableData.filter((row) => visibleColumns.some((col) => String(row[col.key] ?? "").toLowerCase().includes(q)));
  }, [tableData, searchQuery, visibleColumns]);

  // ==================== CONTINUOUS INFINITE SCROLL & KEYBOARD NAV ====================
  const {
    currentPage,
    pageSize,
    pageSizeOptions,
    totalPages,
    totalRows,
    displayedRows,
    selectedIndex,
    setSelectedIndex,
    tableContainerRef,
    handleScroll,
    handleWheel,
    handleKeyDown,
    handlePageChange,
    handlePageSizeChange,
    autoAdvance,
    toggleAutoAdvance,
    isFlipping,
  } = useAutoAdvancePagination({
    items: filteredRows,
    initialPageSize: 100,
    pageSizeOptions: [50, 100, 250, 500],
    initialAutoAdvance: true,
  });

  // Stable column resize handler (used by shared ResizableHeader)
  const handleColumnResize = useCallback((key, newWidth, name) => {
    setGridColumns((prev) => {
      const updated = prev.map((c) => (c.key === key ? { ...c, width: newWidth } : c));
      localStorage.setItem(`grid_design_${name}`, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const { exportingText, runExport } = useReportExport();

  const exportToExcel = () => {
    if (filteredRows.length === 0) { alert("No data available to export!"); return; }
    runExport("Generating Excel Spreadsheet...", () => {
      const worksheet = XLSX.utils.json_to_sheet(filteredRows);
      const workbook  = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "SchemeOverDue");
      XLSX.writeFile(workbook, "SchemeOverDue.xlsx");
    });
  };

  const handleRowClick = (index) => setSelectedRowIndex(index);



  const inputClass = "w-full px-2 text-xs border border-slate-200 rounded-[5px] focus:outline-none focus:ring-1 focus:ring-blue-500 font-Inter font-semibold text-slate-800 bg-white transition-all placeholder-slate-400";

  return (
    <div
      className="bg-[#d2e4fc] w-full h-full rounded-none shadow-2xl flex flex-col items-center absolute left-0 top-0 p-[1%] font-Inter"
      style={{ zIndex }}
    >
      <div className="w-full flex-1 flex gap-3 overflow-hidden relative">

        {/* LEFT: Filter / Parameter Card */}
        <div className={`bg-white border border-[#b8cce0] rounded-[10px] shadow-sm h-full transition-all duration-300 flex flex-col shrink-0 overflow-y-auto ${isSidebarExpanded ? "w-[280px] px-4 py-4" : "w-0 overflow-hidden border-none shadow-none"}`}>
          {isSidebarExpanded && (
            <div className="flex flex-col gap-2.5 font-Inter">

              <div className="flex items-center gap-2 pb-1.5 border-b border-gray-200">
                <img src={headeLogo} alt="Logo" className="h-5 w-auto object-contain" />
                <span className="font-bold text-slate-800 text-sm font-Inter">Report Parameters</span>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-[10px] font-bold text-slate-400 font-Inter uppercase tracking-wider">REPORT SETTINGS</span>

                <div className="flex flex-col gap-0.5">
                  <label className="text-xs text-slate-700 font-bold font-Inter">Report Type:</label>
                  <SherComboBox
                    comboRef={reportTypeRef}
                    options={[
                      { value: "Simple",               label: "Simple" },
                      { value: "Not Paid",             label: "Not Paid" },
                      { value: "Late Paid",            label: "Late Paid" },
                      { value: "Paid On Due Date",     label: "Paid On Due Date" },
                      { value: "Paid Before Due Date", label: "Paid Before Due Date" },
                    ]}
                    value={overdueData.reportType?.value || ""}
                    onSelect={(opt) => setOverdueData({ ...overdueData, reportType: opt })}
                    className="w-full font-Inter"
                    inputClassName={inputClass}
                    inputHeight="28px"
                  />
                </div>

                <div className="flex flex-col gap-0.5">
                  <label className="text-xs text-slate-700 font-bold font-Inter">Scheme:</label>
                  <SherComboBox
                    comboRef={schemeRef}
                    options={schemesList}
                    value={overdueData.scheme?.label || "All Schemes"}
                    onSelect={handleSchemeSelect}
                    className="w-full font-Inter"
                    inputClassName={inputClass}
                    inputHeight="28px"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2 pt-2 border-t border-slate-200">
                <span className="text-[10px] font-bold text-slate-400 font-Inter uppercase tracking-wider">DATE FILTER</span>

                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs text-slate-700 font-bold font-Inter">Due Date:</label>
                    {overdueData.scheme?.dueDay && Number(overdueData.scheme.dueDay) > 0 ? (
                      <span className="text-[10px] text-blue-600 font-semibold bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                        Due Day: {overdueData.scheme.dueDay}th
                      </span>
                    ) : null}
                  </div>
                  <input
                    type="date"
                    value={overdueData.dueDate || ""}
                    onChange={(e) => setOverdueData({ ...overdueData, dueDate: e.target.value })}
                    className={inputClass}
                    style={{ height: "28px" }}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5 mt-2">
                <button
                  onClick={fetchData}
                  className="w-full bg-[#088395] hover:bg-[#066f7f] text-white font-bold py-1.5 rounded shadow-sm text-xs font-Inter border-none cursor-pointer flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  VIEW REPORT
                </button>
                <button
                  onClick={exportToExcel}
                  className="w-full bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 font-bold py-1.5 rounded shadow-sm text-xs font-Inter cursor-pointer flex items-center justify-center gap-2"
                >
                  <img className="h-4 w-auto" src={toExcelIcon} alt="Excel" />
                  EXPORT EXCEL
                </button>
              </div>

            </div>
          )}
        </div>

        {/* Sidebar toggle tab */}
        <div
          onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
          className={`w-[20px] h-[40px] bg-[#1e73be] hover:bg-blue-700 text-white flex items-center justify-center cursor-pointer select-none font-bold shadow-md absolute top-1/2 -translate-y-1/2 z-20 rounded-r transition-all duration-300 ${isSidebarExpanded ? "left-[280px]" : "left-0"}`}
        >
          {isSidebarExpanded ? "«" : "»"}
        </div>

        {/* RIGHT: Report Table Card */}
        <div className="flex-1 h-full bg-white border border-[#b8cce0] rounded-[10px] shadow-sm flex flex-col p-4 overflow-hidden">

          <div className="flex items-center justify-between pb-3 border-b border-gray-200 mb-3 font-Inter">
            <div className="flex items-center gap-3">
              <span className="font-bold text-slate-800 text-lg">Scheme OverDue</span>
              <span className="bg-slate-100 border border-slate-200 text-slate-700 px-2 py-0.5 rounded-full text-[11px] font-semibold">
                Total Records: {filteredRows.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={exportToExcel} className="bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5 cursor-pointer">
                <img className="h-3.5 w-auto" src={toExcelIcon} alt="Excel" /> Excel
              </button>
              <button onClick={fetchData} className="bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5 cursor-pointer">
                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89M9 11l3-3m0 0l3 3m-3-3v12" />
                </svg>
                Refresh
              </button>
              <button onClick={() => setShowGridDesignModal(true)} className="bg-slate-100 hover:bg-slate-200 text-slate-700 p-1.5 rounded text-xs font-bold border-none cursor-pointer flex items-center justify-center" title="Design Columns">⋮</button>
              <button onClick={onClose} className="bg-slate-100 hover:bg-red-50 hover:text-red-600 text-slate-700 p-1.5 rounded text-xs font-bold border-none cursor-pointer flex items-center justify-center ml-1" title="Close">✕</button>
            </div>
          </div>

          {/* Quick Search */}
          <div className="w-full flex justify-end pb-3">
            <div className="relative w-64">
              <input
                type="text"
                placeholder="Search across all columns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-7 py-1.5 text-xs border border-slate-300 rounded-[5px] focus:outline-none focus:ring-1 focus:ring-blue-500 font-Inter font-semibold text-slate-800 bg-white transition-all placeholder-slate-400 h-[28px]"
              />
              <svg className="absolute left-2.5 top-2 h-3.5 w-3.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} className="absolute right-2 top-1.5 text-gray-400 hover:text-gray-600 text-xs font-semibold px-1">✕</button>
              )}
            </div>
          </div>

          {/* Table */}
          <div
            ref={tableContainerRef}
            className="w-full flex-1 min-h-0 overflow-auto border border-slate-300 rounded text-slate-800 text-[11px] font-semibold font-Inter outline-none focus:ring-1 focus:ring-blue-400"
            tabIndex={0}
            onScroll={handleScroll}
            onWheel={handleWheel}
            onKeyDown={handleKeyDown}
          >
            {isLoading ? (
              <div className="w-full h-full flex items-center justify-center bg-white/70">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  <span className="font-Inter text-xs text-blue-900 font-semibold">Loading Overdue Data...</span>
                </div>
              </div>
            ) : (
              <div className="min-w-max flex flex-col min-h-full">

                {/* Sticky Header */}
                <div className="sticky top-0 z-10 bg-[#1e73be] border-b border-slate-300 shrink-0">
                  <div className="grid gap-0 h-8" style={{ gridTemplateColumns }}>
                    <div className="font-Inter text-[11px] font-bold text-white border-r border-slate-300 px-3 flex items-center justify-center">Sl No</div>
                    {visibleColumns.map((col) => (
                      <ResizableHeader key={col.key} col={col} formName={formName} onResize={handleColumnResize}>
                        <div className="font-Inter text-[11px] font-bold text-white border-r border-slate-300 px-3 flex items-center justify-center w-full h-full truncate">
                          {col.header}
                        </div>
                      </ResizableHeader>
                    ))}
                  </div>
                </div>

                {/* Data Rows */}
                <div className="bg-white flex-1">
                  {filteredRows.length === 0 ? (
                    <div className="font-Inter text-xs text-gray-400 text-center py-12">No records to display. Select filters and click VIEW REPORT.</div>
                  ) : (
                    displayedRows.map((row, index) => {
                      const isHighlighted = index === selectedIndex || index === selectedRowIndex;
                      return (
                        <div
                          key={index}
                          data-row-index={index}
                          onClick={() => { handleRowClick(index); setSelectedIndex(index); }}
                          className={`grid gap-0 h-8 border-b border-slate-300 hover:bg-blue-50/40 cursor-pointer transition-colors duration-150 ${isHighlighted ? "bg-blue-100 font-bold text-blue-900 ring-1 ring-inset ring-blue-500" : index % 2 === 0 ? "bg-white" : "bg-[#f2f7ff]"}`}
                          style={{ gridTemplateColumns }}
                        >
                          <div className="font-Inter text-[11px] border-r border-slate-300 px-3 text-slate-700 flex items-center justify-center">{index + 1}</div>
                          {visibleColumns.map((col) => {
                            const val = String(row[col.key] ?? "");
                            const lk  = col.key.toLowerCase();
                            if (lk === "status") {
                              const lowerVal = val.toLowerCase();
                              let badgeStyle = "bg-slate-100 text-slate-700 border border-slate-200";
                              if (lowerVal.includes("paid before") || lowerVal.includes("on due date") || lowerVal === "paid") {
                                badgeStyle = "bg-green-100 text-green-700 border border-green-200";
                              } else if (lowerVal.includes("late")) {
                                badgeStyle = "bg-amber-100 text-amber-800 border border-amber-200";
                              } else if (lowerVal.includes("not paid")) {
                                badgeStyle = "bg-red-100 text-red-700 border border-red-200";
                              }
                              return (
                                <div key={col.key} className="font-Inter text-[11px] border-r border-slate-300 px-3 shrink-0 flex items-center justify-center">
                                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${badgeStyle}`}>{val || "—"}</span>
                                </div>
                              );
                            }
                            const isBlue = lk.includes("name") || lk.includes("cust");
                            return (
                              <div
                                key={col.key}
                                className={`font-Inter text-[11px] border-r border-slate-300 px-3 flex items-center truncate ${isBlue ? "text-[#1e73be] font-medium cursor-pointer hover:underline" : "text-slate-700"} ${col.align === "center" ? "justify-center text-center" : col.align === "right" ? "justify-end text-right" : ""}`}
                              >
                                {val === "null" || val === "-" ? "" : val}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Footer Total Row */}
                {filteredRows.length > 0 && (
                  <div className="grid gap-0 h-8 bg-[#dbeafe] text-blue-900 font-bold sticky bottom-0 z-10 border-t border-slate-300 shrink-0" style={{ gridTemplateColumns }}>
                    <div className="font-Inter text-[11px] border-r border-slate-300 px-3 flex items-center justify-center">Total</div>
                    {visibleColumns.map((col, i) => (
                      <div key={col.key} className="font-Inter text-[11px] border-r border-slate-300 px-3 flex items-center truncate">
                        {i === 0 ? `${filteredRows.length} Records` : ""}
                      </div>
                    ))}
                  </div>
                )}

              </div>
            )}
          </div>
          <TablePagination
            currentPage={currentPage}
            totalRows={totalRows}
            pageSize={pageSize}
            pageSizeOptions={pageSizeOptions}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            autoAdvance={autoAdvance}
            onToggleAutoAdvance={toggleAutoAdvance}
            isFlipping={isFlipping}
          />

        </div>
      </div>

      {/* Grid Design Modal */}
      {showGridDesignModal && (
        <GridDesign
          onClose={() => setShowGridDesignModal(false)}
          onSave={(updated) => { setGridColumns(updated); setShowGridDesignModal(false); }}
          formName={formName}
          initialColumns={gridColumns}
          zIndex={zIndex + 10}
        />
      )}

      {/* Global Export Loader for Heavy Operations */}
      <ExportLoader message={exportingText} />
    </div>
  );
}

export default SchemeOverDue;
